import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  getV1CompaniesCompanyIdProjects,
  GetV1CompaniesCompanyIdProjectsData,
  ProjectState,
} from '@treadinc/horizon-api-spec';
import { noop, omit } from 'lodash-es';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { FetchReason } from '~constants/enums';
import { Project } from '~hooks/useProjects/models';
import { extractPagination, Pagination } from '~services/pagination';

import { PROJECTS_QUERY_CONTEXT_PREFIX } from './constants';
import { makeProjectsQueryContextKey } from './utils';

enum StorageKeys {
  APPLIED_FILTERS = 'projectsQueryContext_appliedFilters',
}

const storeFilters = (filters: ProjectsQueryFilters) => {
  const filtersToStore = omit(filters, ['searchQuery']);

  localStorage.setItem(StorageKeys.APPLIED_FILTERS, JSON.stringify(filtersToStore));
};

const retrieveFilters = () => {
  const storedFilters = localStorage.getItem(StorageKeys.APPLIED_FILTERS);

  if (!storedFilters) {
    return null;
  }

  const filters = JSON.parse(storedFilters) as ProjectsQueryFilters;

  return filters;
};

const derivePaginationQueryParams = (pageParam?: Pagination) => {
  return {
    'page[limit]': 20,
    'page[after]': pageParam?.after,
  };
};

const deriveFilterQueryParams = (filters: ProjectsQueryFilters) => {
  const {
    searchQuery,
    collaboratorIds,
    departmentIds,
    dropoffSiteIds,
    foremanIds,
    materialIds,
    pickupSiteIds,
    requesterIds,
    salespersonIds,
    states,
  } = filters;

  const query: GetV1CompaniesCompanyIdProjectsData['query'] & Record<string, unknown> = {
    'search[datagrid]': searchQuery || undefined,
    'filter[collaborator_ids]': collaboratorIds?.length ? collaboratorIds : undefined,
    'filter[department_ids]': departmentIds?.length ? departmentIds : undefined,
    'filter[dropoff_site_ids]': dropoffSiteIds?.length ? dropoffSiteIds : undefined,
    'filter[foreman_ids]': foremanIds?.length ? foremanIds : undefined,
    'filter[material_ids]': materialIds?.length ? materialIds : undefined,
    'filter[pickup_site_ids]': pickupSiteIds?.length ? pickupSiteIds : undefined,
    'filter[requester_ids]': requesterIds?.length ? requesterIds : undefined,
    'filter[salesperson_ids]': salespersonIds?.length ? salespersonIds : undefined,
    'filter[states]': states?.length ? states : undefined,
  };

  return query;
};

type ProjectsQueryFilters = {
  searchQuery?: string;
  collaboratorIds?: string[];
  departmentIds?: string[];
  dropoffSiteIds?: string[];
  foremanIds?: string[];
  materialIds?: string[];
  pickupSiteIds?: string[];
  requesterIds?: string[];
  salespersonIds?: string[];
  states?: ProjectState[];
};

type ProjectsQueryParams = {
  filters: ProjectsQueryFilters;
  isRefreshing?: boolean;
  pagination?: Pagination;
};

type ProjectsQueryContextType = {
  applyFilters: (partialFilters: Partial<ProjectsQueryFilters>) => void;
  applySearchQuery: (searchQuery: string) => void;
  clearQueryData: () => void;
  data: Project[];
  fetchMore: () => void;
  fetchReason?: FetchReason;
  filters: ProjectsQueryFilters;
  hasMore: boolean;
  refresh: () => void;
  removeAllFilters: (filterKeys: string[]) => void;
  removeStoredProject: (project: Project) => void;
  updateStoredProject: (project: Project) => void;
};

const ProjectsQueryContext = createContext<ProjectsQueryContextType>({
  applyFilters: noop,
  applySearchQuery: noop,
  clearQueryData: noop,
  data: [],
  fetchMore: noop,
  filters: {},
  hasMore: false,
  refresh: noop,
  removeAllFilters: noop,
  removeStoredProject: noop,
  updateStoredProject: noop,
});

function ProjectsQueryContextProvider({
  companyId,
  children,
  initialFilters,
}: React.PropsWithChildren<{
  companyId: string;
  initialFilters?: Partial<ProjectsQueryFilters>;
}>) {
  const queryKey = makeProjectsQueryContextKey();
  const queryClient = useQueryClient();

  const defaultFilters: ProjectsQueryFilters = {};
  const storedFilters = retrieveFilters();
  const mergedInitialFilters: ProjectsQueryFilters = {
    ...defaultFilters,
    ...(storedFilters ?? {}),
    ...(initialFilters ?? {}),
  };

  const [queryParams, setQueryParams] = useState<ProjectsQueryParams>({
    filters: mergedInitialFilters,
  });

  const query = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: async ({ pageParam, signal }) => {
      const { filters } = queryParams;

      const query: GetV1CompaniesCompanyIdProjectsData['query'] = {
        ...derivePaginationQueryParams(pageParam),
        ...deriveFilterQueryParams(filters),
      };

      const response = await getV1CompaniesCompanyIdProjects({
        path: { 'company-id': companyId },
        query,
        signal,
      });

      const projects = response.data.data.map((project) => Project.parse(project));
      const pagination = extractPagination(response);

      setQueryParams((state) => ({ ...state, pagination }));

      return projects;
    },
  });

  const hasMore = Boolean(queryParams.pagination?.after);

  const data = useMemo(() => (query.data?.pages ?? []).flat(), [query.data?.pages]);

  const fetchReason = useMemo(() => {
    if (query.isFetchingNextPage) {
      return FetchReason.INFINITE_SCROLL;
    }

    if (query.isFetching) {
      return FetchReason.INITIAL_FETCH;
    }
  }, [query.isFetchingNextPage, query.isFetching]);

  const fetchMore = useCallback(() => {
    if (hasMore) {
      query.fetchNextPage();
    }
  }, [hasMore]);

  const clearQueryData = useCallback(() => {
    queryClient.removeQueries({
      predicate(query) {
        return query.queryKey.some((key) => {
          const keyAsString = String(key);

          return (
            keyAsString.startsWith(PROJECTS_QUERY_CONTEXT_PREFIX) ||
            keyAsString.startsWith(queryKey)
          );
        });
      },
    });
  }, [queryKey]);

  const applySearchQuery = useCallback<ProjectsQueryContextType['applySearchQuery']>(
    (searchQuery) => {
      clearQueryData();

      const isClearingSearchQuery = searchQuery.trim().length === 0;

      if (isClearingSearchQuery) {
        const filters = retrieveFilters() ?? { ...defaultFilters };

        setQueryParams({ filters });
      } else {
        setQueryParams((state) => {
          storeFilters(state.filters);

          return { filters: { ...state.filters, searchQuery } };
        });
      }
    },
    [clearQueryData],
  );

  const applyFilters = useCallback<ProjectsQueryContextType['applyFilters']>(
    (partialFilters) => {
      clearQueryData();

      setQueryParams((state) => {
        const newFilters = { ...state.filters, ...partialFilters };

        storeFilters(newFilters);

        return { filters: newFilters };
      });
    },
    [clearQueryData],
  );

  const removeAllFilters = useCallback<ProjectsQueryContextType['removeAllFilters']>(
    (filterKeys) => {
      clearQueryData();

      setQueryParams((state) => {
        const newFilters = { ...state.filters };

        filterKeys.forEach((key) => {
          (newFilters as Record<string, unknown>)[key] = undefined;
        });

        storeFilters(newFilters);

        return { filters: newFilters };
      });
    },
    [clearQueryData],
  );

  const removeStoredProject = useCallback<
    ProjectsQueryContextType['removeStoredProject']
  >(
    (project) => {
      queryClient.setQueryData(
        [queryKey, queryParams.filters],
        (oldProjects?: {
          pages: Array<Project[]>;
          pageParams: Array<Pagination | undefined>;
        }) => {
          if (!oldProjects) {
            return undefined;
          }

          return {
            ...oldProjects,
            pages: oldProjects.pages.map((oldProjectsPage) => {
              return oldProjectsPage.filter((oldProject) => oldProject.id !== project.id);
            }),
          };
        },
      );
    },
    [queryKey, queryParams.filters],
  );

  const updateStoredProject = useCallback<
    ProjectsQueryContextType['updateStoredProject']
  >(
    (project) => {
      queryClient.setQueryData(
        [queryKey, queryParams.filters],
        (oldProjects?: {
          pages: Array<Project[]>;
          pageParams: Array<Pagination | undefined>;
        }) => {
          if (!oldProjects) {
            return undefined;
          }

          const { dropoffSiteIds, materialIds, pickupSiteIds, states } =
            queryParams.filters;
          const matchesActiveStateFilter =
            !states?.length || (project.state ? states.includes(project.state) : false);
          const matchesActivePickupSiteFilter =
            !pickupSiteIds?.length ||
            Boolean(
              project.pickUpWayPoints?.some(
                (waypoint) => waypoint.site && pickupSiteIds.includes(waypoint.site.id),
              ),
            );
          const matchesActiveDropoffSiteFilter =
            !dropoffSiteIds?.length ||
            Boolean(
              project.dropOffWayPoints?.some(
                (waypoint) => waypoint.site && dropoffSiteIds.includes(waypoint.site.id),
              ),
            );
          const matchesActiveMaterialFilter =
            !materialIds?.length ||
            Boolean(
              project.projectMaterialTotals?.some(
                (materialTotal) =>
                  materialTotal.material &&
                  materialIds.includes(materialTotal.material.id),
              ),
            );

          if (
            !matchesActiveStateFilter ||
            !matchesActivePickupSiteFilter ||
            !matchesActiveDropoffSiteFilter ||
            !matchesActiveMaterialFilter
          ) {
            return {
              ...oldProjects,
              pages: oldProjects.pages.map((oldProjectsPage) =>
                oldProjectsPage.filter((oldProject) => oldProject.id !== project.id),
              ),
            };
          }

          let founded = false;

          const newProjects = {
            ...oldProjects,
            pages: oldProjects.pages.map((oldProjectsPage) => {
              return oldProjectsPage.map((oldProject) => {
                if (oldProject.id === project.id) {
                  founded = true;

                  return project;
                }

                return oldProject;
              });
            }),
          };

          if (!founded) {
            if (newProjects.pages?.[0]) {
              newProjects.pages[0].unshift(project);
            } else {
              newProjects.pages = [[project]];
            }
          }

          return newProjects;
        },
      );
    },
    [queryKey, queryParams.filters],
  );

  const refresh = useCallback<ProjectsQueryContextType['refresh']>(() => {
    setQueryParams((state) => ({
      filters: { ...state.filters },
      isRefreshing: true,
    }));
  }, [clearQueryData]);

  useEffect(() => {
    if (queryParams.isRefreshing) {
      setQueryParams((state) => omit(state, 'isRefreshing'));

      clearQueryData();
      query.refetch();
    }
  }, [queryParams.isRefreshing, clearQueryData]);

  return (
    <ProjectsQueryContext.Provider
      value={{
        applyFilters,
        applySearchQuery,
        clearQueryData,
        data,
        fetchMore,
        fetchReason,
        filters: queryParams.filters,
        hasMore,
        refresh,
        removeAllFilters,
        removeStoredProject,
        updateStoredProject,
      }}
    >
      {children}
    </ProjectsQueryContext.Provider>
  );
}

export default ProjectsQueryContext;
export { ProjectsQueryContextProvider };
